/**
 * @module geo/functionalities/services
 */
import {Observable} from 'rxjs/Observable';

import {FeatureTransactionService} from '../../services/feature.transaction';
import {ServiceLoader} from '../../services/service.loader';
import {Feature} from '../../types/feature';
import {FeatureTransactionOperation, FeatureTransactionResult, OperationType} from '../../types/feature.transaction.operation';
import {GeometryModificationFunctionality} from '../geometry.modification';

import {FunctionalityService} from '.';


/**
 * A geometryModification service to edit feature on geometry modification
 *
 * @since v2.1.0
 */
export class GeometryModificationFunctionalityService extends
    FunctionalityService<
        GeometryModificationFunctionality, FeatureTransactionService> {
  protected functionality: GeometryModificationFunctionality;
  protected parentService: FeatureTransactionService;

  /**
   * list of current operations on source.
   */
  public operations: FeatureTransactionOperation[];

  constructor(functionality: GeometryModificationFunctionality, options: any) {
    super();
    this.functionality = functionality;
    this.parentService = ServiceLoader.getServiceInstance(
        'FeatureTransaction', options.store.getState().uid, options);

    this.operations = [];
  }

  /**
   * add Features on current source (geometry modification)
   * will commit all current modification by default (set commit to false to
   * register modification and commit all in one)
   *
   * return promise with result of commit or operations to commit
   *
   * Example :
   * ```
   * geoApp.getFunctionalityById("xxx")
   *  .getService()
   *  .addFeatures([
   *  {
   *               type: 'Feature',
   *               writeGeometry: true,
   *               geometry: {
   *                   "type": "Polygon",
   *                   "coordinates": [[[402398.56692069094, 6341963.415311435],
   * [353478.86881817854, 6553948.674228277], [869853.3935486709,
   * 6640917.1425258], [804627.2289393677, 6380012.036215374],
   * [625255.0523271777, 6276737.101411061], [402398.56692069094,
   * 6341963.415311435]]], "crs": "EPSG:2154"
   *               },
   *               properties: { "unevaleur": "42", "nom": "testapi" },
   *
   *  }]).subscribe(
   *      (results)=>{
   *          // doSomething with results
   *      }
   * );
   * ```
   */
  public addFeatures(features: Feature[], commit: boolean = true):
      Observable<FeatureTransactionResult[]|FeatureTransactionOperation[]> {
    const sourceId = this.functionality.id;
    const _features = features;
    const newOp = this.parentService.buildOperation(
        _features, sourceId, OperationType.INSERT);
    this.operations = this.operations.concat(newOp);

    if (commit) {
      return this._commit(newOp);
    } else {
      this.operations = this.operations.concat(newOp);
      return Observable.of(newOp)
    }
  }

  /**
   * delete features on current source (geometry modification)
   * will commit all current modification by default (set commit to false to
   * register modification and commit all in one)
   *
   * return promise with result of commit or operations to commit
   */
  public deleteFeatures(features: Feature[], commit: boolean = true):
      Observable<FeatureTransactionResult[]|FeatureTransactionOperation[]> {
    const sourceId = this.functionality.id;
    const _features = features;
    const newOp = this.parentService.buildOperation(
        _features, sourceId, OperationType.DELETE);
    this.operations = this.operations.concat(newOp);

    if (commit) {
      return this._commit(newOp);
    } else {
      this.operations = this.operations.concat(newOp);
      return Observable.of(newOp);
    }
  }

  /**
   * update features on current source (geometry modification)
   * will commit all current modification by default (set commit to false to
   * register modification and commit all in one)
   *
   * return promise with result of commit or operations to commit
   */
  public updateFeatures(features: Feature[], commit: boolean = true):
      Observable<FeatureTransactionResult[]|FeatureTransactionOperation[]> {
    const sourceId = this.functionality.id;
    const _features = features;
    const newOp = this.parentService.buildOperation(
        _features, sourceId, OperationType.UPDATE);
    if (commit) {
      return this._commit(newOp);
    } else {
      this.operations = this.operations.concat(newOp);
      return Observable.of(newOp)
    }
  }

  /**
   * commit all current modification and reset list of operations
   * return a promise with result of commit
   */
  public commit(): Observable<FeatureTransactionResult[]|null> {
    if (this.operations.length > 0) {
      return this._commit(this.operations).do(() => {
        this.operations = [];
      });
    } else {
      return Observable.of(null)
    }
  }

  /**
   *
   * @private
   */
  private _commit(operations: FeatureTransactionOperation[]):
      Observable<FeatureTransactionResult[]> {
    return this.parentService.save(operations);
  }
}
